
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { ChartDataPoint, PuttingResult } from "@/types/puttingResults";
import { useChartData } from "./charts/useChartData";
import EmptyChartMessage from "./charts/EmptyChartMessage";
import ChartHeader from "./charts/ChartHeader";
import SeparateCharts from "./charts/SeparateCharts";
import CombinedChart from "./charts/CombinedChart";

interface MultiTestResultsChartProps {
  chartDataByTest: Record<string, ChartDataPoint[]>;
  testTypes: string[];
  selectedLevel: string | null;
  trainingResults: PuttingResult[];
}

const MultiTestResultsChart: React.FC<MultiTestResultsChartProps> = ({ 
  chartDataByTest,
  testTypes,
  selectedLevel,
  trainingResults
}) => {
  const [selectedView, setSelectedView] = useState<"separate" | "combined">("separate");
  
  // Get filtered data based on selected level
  const { filteredTestTypes, filteredChartData } = useChartData(
    chartDataByTest,
    testTypes,
    selectedLevel,
    trainingResults
  );
  
  // Si aucune donnée, affiche un message
  const hasTestData = filteredTestTypes.length > 0;
  
  // Set fixed domain values to ensure chart is always visible
  const pointsDomain: [number, number] = [0, 15]; // Points typically between 0-15

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        {!hasTestData ? (
          <EmptyChartMessage 
            selectedView={selectedView} 
            onViewChange={setSelectedView} 
          />
        ) : (
          <>
            <ChartHeader 
              selectedView={selectedView} 
              onViewChange={setSelectedView} 
            />
            <div className="mt-4">
              {selectedView === "separate" ? (
                <SeparateCharts 
                  filteredTestTypes={filteredTestTypes}
                  filteredChartData={filteredChartData}
                  pointsDomain={pointsDomain}
                />
              ) : (
                <CombinedChart 
                  filteredTestTypes={filteredTestTypes}
                  filteredChartData={filteredChartData}
                  pointsDomain={pointsDomain}
                />
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MultiTestResultsChart;
